import { Link } from 'react-router-dom';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ICONS, IntroContent, PATHS, USER_ROLES } from 'src/common/constants';
import { selectServiceRequestFormIsVisible } from 'src/store/selectors/serviceRequestForm';
import {
  exitServiceRequestForm,
  setServiceRequestFormIsVisible,
} from 'src/store/actions/serviceRequestForm';
import { selectSearch } from 'src/store/selectors/search';
import Button from '../Button';
import Empty from '../Empty';
import IcoMoon from '../IcoMoon';
import FontAwesomeIcon from '../Icon';
import Label from '../Label';
import Overlay from '../Overlay';
import ServiceRequestForm from '../ServiceRequestForm/ServiceRequestForm';
import Status from '../Status';
import GroupingTable from '../GroupingTable';
import { selectServiceRequests } from 'src/store/selectors/serviceRequests';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { serviceRequestCsvHeaders as headers } from '../../services/csv/serviceRequestCsvHeaders';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  createServiceRequestByGroup,
  fetchGroupServiceRequest,
  updateBulkStatus,
} from 'src/store/actions/serviceRequests';
import {
  clearServiceRequestByGroup,
  deleteServiceRequestFromGroup,
  deleteServiceRequestGroup,
  setCurrentGroupServiceRequestIndex,
} from 'src/store/actions/serviceRequestsByGroup';
import { addFilterQuery } from 'src/store/actions/Filter';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import Checkbox from '../Checkbox';
import Popup from '../../pages/ServiceRequest/Popup';
import { Popup as Modal } from 'reactjs-popup';
import { Filters as FilterModal } from './Filters';
import { selectProperties } from 'src/store/selectors/properties';
import { selectItems } from 'src/store/selectors/items';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getBuildingLevels,
  getPropertyBuildings,
} from 'src/store/actions/buildings';
import ServiceRequestAssignForm from '../../pages/ServiceRequest/ServiceRequestAssignForm';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import { clearLoadingDownloadServiceRequestList } from 'src/store/actions/csvExport';
import { variables } from 'src/Tenants';
import {
  fetchAllIssueTypes,
  fetchAllTicketStatus,
} from 'src/store/actions/formOptions';
import ContentBlock from '../ContentBlock';
import {
  clearSelectedPropertyCommonAreas,
  fetchPropertyCommonAreas,
} from 'src/store/actions/properties';
import { commonAreaName, sortableType, suiteName } from './transformations';
import { sortByDate } from '../Table/sorting';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { filterAllowedStatus } from 'src/pages/ServiceRequests/helpers/allowedStatus';
import { generateServiceRequestTableColumns } from 'src/components/ServiceRequestsTable/helpers';
import GroupingModal from 'src/components/GroupingModal/GroupingModal';
import DownloadCSVModalLegacy from 'src/components/DownloadModal/DownloadCSVModalLegacy';
import ActiveFiltersDisplay from 'src/components/ActiveFiltersDisplay/ActiveFIltersDisplay';
import SearchAndActionsBar from 'src/components/SearchAndActionsBar/SearchAndActionsBar';
import './serviceRequestsTable.scss';
import useFetchProperties from 'src/pages/SharedPageCustomHooks/UseFetchProperties/useFetchProperties';
import { useDownloadPDF } from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import { useDownloadCSVData } from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';

let statuses: any = [
  { label: 'Submitted', key: 'Submitted', id: 'submitted' },
  { label: 'Scheduled', key: 'Scheduled', id: 'scheduled' },
  { label: 'En Route', key: 'En Route', id: 'enRoute' },
  { label: 'On Hold', key: 'On Hold', id: 'onHold' },
  { label: 'In Progress', key: 'In Progress', id: 'inProgress' },
  { label: 'Resolved', key: 'Resolved', id: 'resolved' },
  { label: 'Declined', key: 'Declined', id: 'declined' },
  { label: 'Closed', key: 'Closed', id: 'closed' },
  { label: 'Archive', key: 'Archive', id: 'archive' },
  { label: 'Assigned', key: 'Assigned', id: 'assigned' },
  { label: 'Under Review', key: 'Under Review', id: 'underReview' },
  { label: 'Approved', key: 'Approved', id: 'approved' },
  {
    label: 'One Year Warranty',
    key: 'One Year Warranty',
    id: 'oneYearWarranty',
  },
];

const WarrantyDisabledMessage = ({ warrantyStartDate }) => {
  const formatWarrantyDate = (dateString: any) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div
      style={{
        color: '#f56565',
        fontSize: '14px',
        fontWeight: '500',
        textAlign: 'left',
        marginTop: '8px',
        maxWidth: '600px',
      }}
    >
      Homeowner Service Requests cannot be submitted yet. Your warranty coverage
      begins on <strong>{formatWarrantyDate(warrantyStartDate)}</strong>. Please
      wait until this date or contact your property developer for assistance.
    </div>
  );
};

const ServiceRequestsTable = ({ serviceRequests, ...tableProps }) => {
  if (!serviceRequests || serviceRequests.length === 0) return null;

  return (
    <div id="scrollableDiv" className="serviceRequestsTable__container">
      <InfiniteScroll
        dataLength={serviceRequests.length}
        style={{ display: 'flex', flexDirection: 'column' }}
        inverse={false}
        hasMore={tableProps.hasMoreRecords}
        loader={
          tableProps.paginationCount === tableProps.pageCount ||
          serviceRequests.length === 1 ? (
            <div />
          ) : (
            <h4>Loading...</h4>
          )
        }
        scrollableTarget="scrollableDiv"
        next={tableProps.fetchMoreData}
      >
        <GroupingTable {...tableProps} data={serviceRequests} />
      </InfiniteScroll>
    </div>
  );
};

const EmptyState = ({
  user,
  variables,
  search,
  strapline,
  itemView,
  dispatch,
}: any) => (
  <Empty
    direction="ttb"
    headline={`Your ${variables.serviceRequest.toLowerCase()} inbox is empty`}
    strapline={
      strapline && strapline.length > 0
        ? strapline
        : `All ${variables.serviceRequest.toLowerCase()}s` +
          (user.roles[0].role.name === 'Admin' ? ' ' : ' you ') +
          'add will be listed here so its easier for you to manage.'
    }
    searchIsActive={
      search && search.serviceRequests ? search.serviceRequests : ''
    }
    cta={
      (itemView && itemView === true) ||
      user.roles[0].role.name === 'Vendor' ||
      user.roles[0].role.name === 'Service Provider' ||
      user.roles[0].role.name === 'Realtor' ? null : (
        <Button
          color="teal"
          label={'Add ' + variables.serviceRequest}
          onClick={() => dispatch(setServiceRequestFormIsVisible(true))}
          icon={
            <FontAwesomeIcon glyph="inbox-in" weight="light" size="small" />
          }
          size="small"
        />
      )
    }
    image={
      <img
        alt={`Your ${variables.serviceRequest} inbox is empty`}
        src="/assets/empty/service-requests.svg"
      />
    }
  />
);

// Todo: We need to migrate away from this legacy BOS UI logic for service requests in these tables.
const ServiceRequestsTableMain = (props: any): any => {
  const dispatch = useDispatch();
  const { branding } = useBranding();

  const allowedStatuses = React.useMemo(() => {
    return filterAllowedStatus(statuses, branding);
  }, [branding]);

  const paginationCount = useSelector(
    (state: any) => state.paginationCount.count
  );
  const pageCount = useSelector(
    (state: any) => state.serviceRequestStatusPageCount.pageCount
  );
  const serviceRequests = useSelector(selectServiceRequests);
  const serviceRequestByGroup = useSelector(
    (state: any) => state.serviceRequestsByGroup.serviceRequestByGroup
  );
  const serviceRequestByGroupIndex = useSelector(
    (state: any) => state.serviceRequestsByGroup.indexID
  );
  const displayClosedAndResolvedTickets = useSelector(
    (state: any) => state.serviceRequestsByGroup.displayClosedAndResolvedTickets
  );
  const serviceRequestFormIsVisible = useSelector(
    selectServiceRequestFormIsVisible
  );
  const search = useSelector(selectSearch);
  const user = useSelector(selectAuthenticatedUser);
  const formLoader = useSelector(selectFormLoader);
  const csvExportLoading = useSelector((state: any) => state.csvExport.loading);
  const properties = useSelector(selectProperties);
  const issueTypes = useSelector((state: any) => state.formOptions.types);
  const items = useSelector(selectItems);
  const buildingsList = useSelector((state: any) => state.buildings);
  const levelsList = useSelector((state: any) => state.levels);
  const filterQuery = useSelector((state: any) => state.Filter);
  const filterServiceProviderandPropertyDeveloperList = useSelector(
    (state: any) => state.FilterServiceProviderandPropertyDeveloperList.payload
  );
  const selectedPropertyCommonAreas = useSelector(
    (state: any) => state.FilterCommonAreas.selectedPropertyCommonAreas
  );
  const fetchedPropertyCommonAreas = useSelector(
    (state: any) => state.FilterCommonAreas.fetchedPropertyCommonAreas
  );

  const { fetchPropertySuites, data } = useFetchProperties({});

  const {
    isLoading: isLoadingPDF,
    onDownloadPDF,
    clearError: clearPDFError,
  } = useDownloadPDF();

  const { isLoading, onDownloadCSV } = useDownloadCSVData();

  const [state, setState] = useState<any>({
    expandedGroupIds: [],
    groupData: {},
    hasMoreRecords: true,
    selectedIds: [],
    selectedServiceRequests: [],
    selectedGroupServiceRequestId: [],
    groupServiceRequestObj: [],
    showGroupServiceRequest: false,
    selectedGroupId: '',
    isGroupSelected: false,
    selectedSrsGroupId: [],
    selectedGroupServiceRequest: null,
    openServiceRequestModal: false,
    openDownloadModal: false,
    acceptGroupLabelInput: false,
    groupLabel: '',
    overlayServiceRequest: [],
    showOverlay: false,
    status: 'BULK STATUS UPDATE',
    selectAll: false,
    check: false,
    showPopup: false,
    openFiltersModal: false,
    filterValues: {
      walkthrough: [],
      startDate: '',
      endDate: '',
      project: '',
      issueType: '',
      assetType: '',
      building: '',
      level: '',
      suite: [],
      tags: [],
    },
    initialFetchServiceRequestListParams: {},
    loading: false,
  });

  useEffect(() => {
    let role = user.roles[0].role.name;

    if (
      role === 'Property Developer' ||
      role === 'Strata Manager' ||
      role === 'Service Provider' ||
      role === 'Service Manager'
    ) {
      let filterEstimatedDays = headers.filter(
        (x) => x.label === 'Estimated Days'
      );
      let filterEstimatedMinutes = headers.filter(
        (x) => x.label === 'Estimated Minutes'
      );
      let filterEstimatedHours = headers.filter(
        (x) => x.label === 'Estimated Hours'
      );
      let filterActualDays = headers.filter((x) => x.label === 'Actual Days');
      let filterActualHours = headers.filter((x) => x.label === 'Actual Hours');
      let filterActualMinutes = headers.filter(
        (x) => x.label === 'Actual Minutes'
      );

      if (filterEstimatedDays.length === 0) {
        headers.push({ label: 'Estimated Days', key: 'estimatedDays' });
      }

      if (filterEstimatedHours.length === 0) {
        headers.push({ label: 'Estimated Hours', key: 'estimatedHours' });
      }

      if (filterEstimatedMinutes.length === 0) {
        headers.push({ label: 'Estimated Minutes', key: 'estimatedMinutes' });
      }

      if (filterActualDays.length === 0) {
        headers.push({ label: 'Actual Days', key: 'actualDays' });
      }

      if (filterActualHours.length === 0) {
        headers.push({ label: 'Actual Hours', key: 'actualHours' });
      }

      if (filterActualMinutes.length === 0) {
        headers.push({ label: 'Actual Minutes', key: 'actualMinutes' });
      }
    }

    dispatch(clearLoadingDownloadServiceRequestList());
    dispatch(clearServiceRequestByGroup());
    dispatch(fetchAllTicketStatus());
    dispatch(fetchAllIssueTypes());
    dispatch(exitServiceRequestForm());

    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setTimeout(() => {
      setState((prevState: any) => ({
        ...prevState,
        initialFetchServiceRequestListParams: {
          userID: user.id,
          role: user.roles[0].role.name,
          status: localStorage.getItem('status'),
          cdays: cdays,
          count: 1,
          text:
            localStorage.getItem('searchString') !== '' &&
            localStorage.getItem('searchString') != null
              ? localStorage.getItem('searchString')
              : props.searchString,
          search:
            localStorage.getItem('searchString') !== '' &&
            localStorage.getItem('searchString') != null &&
            props.searchString !== '' &&
            props.searchString != null,
          filterValues: filterQuery,
        },
      }));

      if (filterQuery.commonAreas === '') {
        dispatch(clearSelectedPropertyCommonAreas());
      }
    }, 1000);
  }, [dispatch, user, props.searchString, filterQuery]);

  const isGroupExpanded = (groupData: any) => {
    return state.expandedGroupIds.includes(groupData.id);
  };

  const expandGroup = (groupData: any) => {
    setState((previousState: any) => ({
      ...previousState,
      expandedGroupIds: [...previousState.expandedGroupIds, groupData.id],
      isGroupExpanded: true,
    }));

    if (!Object.keys(serviceRequestByGroup).includes(groupData.id)) {
      fetchGroupServiceRequestHandler(groupData.id);
    }
  };

  const collapseGroup = (groupData: any) => {
    deselectAllBySRsGroup(groupData);
    dispatch(clearServiceRequestByGroup(groupData.id));
    const filteredGroupIds = state.expandedGroupIds.filter(
      (id: any) => id !== groupData.id
    );
    const filteredSelectedGroups = state.selectedSrsGroupId.filter(
      (group: any) => group.id !== groupData.id
    );

    setState((previousState: any) => ({
      ...previousState,
      expandedGroupIds: filteredGroupIds,
      selectedSrsGroupId: filteredSelectedGroups,
    }));
  };

  const collapseAllGroups = (groupData: any) => {
    dispatch(clearServiceRequestByGroup());
    setState((prevState: any) => ({ ...prevState, expandedGroupIds: [] }));
  };

  const fetchMoreData = () => {
    const {
      pageCount: propPageCount,
      paginationCount: propPaginationCount,
      mainServiceRequest,
    } = props;
    if (mainServiceRequest === true) {
      const { status, days } = props.stateData;
      if (propPaginationCount === propPageCount) {
        setState((prevState: any) => ({ ...prevState, hasMoreRecords: false }));
      }
      props.loadServiceRequest(status, days);
    } else {
      if (propPaginationCount === propPageCount) {
        setState((prevState: any) => ({ ...prevState, hasMoreRecords: false }));
      }
      props.loadServiceRequest();
    }
    setState((prevState: any) => ({ ...prevState, selectAll: false }));
  };

  const onClickBulkUpdate = (id: any, data: any) => {
    let selectedServiceRequests = state.selectedServiceRequests;
    let selectedIds = state.selectedIds;
    let index = selectedIds.findIndex((object: any) => object.id === id);
    if (index === -1) {
      selectedIds = [...selectedIds, { id: id }];
      setState((prevState: any) => ({ ...prevState, selectedIds }));
      setState((prevState: any) => ({
        ...prevState,
        selectedServiceRequests: [...state.selectedServiceRequests, data],
      }));
    } else {
      selectedIds = selectedIds.filter((data: any) => data.id !== id);
      selectedServiceRequests = selectedServiceRequests.filter(
        (data: any) => data.id !== id
      );
      setState((prevState: any) => ({
        ...prevState,
        selectedIds,
        selectedServiceRequests,
      }));
    }
    setState((prevState: any) => ({ ...prevState, selectAll: false }));
  };

  const onChangeStatus = async (e: any) => {
    const eventTarget: string = e?.target?.value;

    if (!eventTarget) {
      return console.error('No valid event found here');
    }

    setState((prevState: any) => ({ ...prevState, status: eventTarget }));

    let params: any = {
      serviceRequest: state.selectedIds,
      name: e.target.value,
      userId: user.id,
      propertyId: props.propertyId,
    };

    if (e.target.value === 'Declined') {
      setState((prevState: any) => ({ ...prevState, showPopup: true }));
    } else {
      if (e.target.value === 'Assigned') {
        setState((prevState: any) => ({ ...prevState, showOverlay: true }));
        setState((prevState: any) => ({
          ...prevState,
          overlayServiceRequest: state.selectedServiceRequests,
          status: 'BULK STATUS UPDATE',
          selectAll: false,
        }));
      } else {
        dispatch(
          updateBulkStatus(
            params,
            user.roles[0].role.name,
            state.initialFetchServiceRequestListParams,
            props.from
          )
        );
        dispatch(clearServiceRequestByGroup());
        state.selectedGroupServiceRequestId.pop();
        setState((prevState: any) => ({
          ...prevState,
          selectedIds: [],
          overlayServiceRequest: state.selectedServiceRequests,
          selectedServiceRequests: [],
          status: 'BULK STATUS UPDATE',
          selectAll: false,
          isGroupSelected: false,
          selectedSrsGroupId: [],
        }));
      }
    }
  };

  const handleStatus = async (data: any, id: any) => {
    let params = {
      serviceRequest: state.selectedIds,
      name: 'Assigned',
      userId: user.id,
      ServiceProviderId: data.userId,
    };
    dispatch(
      updateBulkStatus(
        params,
        user.roles[0].role.name,
        state.initialFetchServiceRequestListParams,
        props.from
      )
    );
    dispatch(clearServiceRequestByGroup());
    state.selectedGroupServiceRequestId.pop();
    setState((prevState: any) => ({
      ...prevState,
      selectedIds: [],
      showOverlay: false,
      overlayServiceRequest: state.selectedServiceRequests,
      selectedServiceRequests: [],
      status: 'BULK STATUS UPDATE',
      selectAll: false,
      isGroupSelected: false,
      selectedSrsGroupId: [],
    }));
  };

  const onSubmitPopup = async (type: any, data: any) => {
    if (type === 'cancel') {
      setState((prevState: any) => ({
        ...prevState,
        showPopup: false,
        selectedIds: [],
        status: 'BULK STATUS UPDATE',
        selectAll: false,
        isGroupSelected: false,
        selectedSrsGroupId: [],
      }));
    } else {
      let params = {
        serviceRequest: state.selectedIds,
        name: 'Declined',
        userId: user.id,
        Reason: data.description,
        ReasonType: data.reason,
      };
      setState((prevState: any) => ({ ...prevState, showPopup: false }));

      dispatch(
        updateBulkStatus(
          params,
          user.roles[0].role.name,
          state.initialFetchServiceRequestListParams,
          props.from
        )
      );
      dispatch(clearServiceRequestByGroup());
      state.selectedGroupServiceRequestId.pop();
      setState((prevState: any) => ({
        ...prevState,
        selectedIds: [],
        status: 'BULK STATUS UPDATE',
        selectAll: false,
        isGroupSelected: false,
        selectedSrsGroupId: [],
      }));
    }
  };

  const selectAll = () => {
    let selectedIds = [];
    let selectedGroups = [];

    if (state.selectAll === false) {
      serviceRequests.forEach((s: any) => {
        if (s.requestType === 'Grouping') {
          if (
            state.selectedSrsGroupId.findIndex((id: any) => s.id === id.id) ===
            -1
          ) {
            if (!isGroupExpanded(s)) {
              expandGroup(s);
            }
            selectedGroups.push(s);
          }
        } else {
          selectedIds = [...selectedIds, { id: s.id }];
        }
      });
      setState((prevState: any) => ({
        ...prevState,
        selectedIds: selectedIds,
        selectedServiceRequests: serviceRequests,
        selectedSrsGroupId: selectedGroups,
        loading: false,
      }));
      setTimeout(
        () =>
          selectedGroups.forEach((group: any) => selectAllBySRsGroup(group)),
        500
      );
    } else {
      setState((prevState: any) => ({
        ...prevState,
        selectedIds: [],
        selectedServiceRequests: [],
        expandedGroupIds: [],
        selectedSrsGroupId: [],
      }));
      collapseAllGroups({});
    }

    setState((prevState: any) => ({
      ...prevState,
      selectAll: !state.selectAll,
    }));
  };

  const selectAllBySRsGroup = (data: any) => {
    setState((prevState: any) => ({ ...prevState, loading: true }));

    let selectedIds = [];
    const serviceRequestByGroupData = serviceRequestByGroup[data.id];

    if (serviceRequestByGroupData?.length > 0) {
      selectedIds = serviceRequestByGroupData.map((s: any) => ({ id: s.id }));
      setState((prevState: any) => ({
        ...prevState,
        selectedIds: [...state.selectedIds, ...selectedIds],
        selectedServiceRequests: serviceRequestByGroupData,
      }));
    }

    setState((prevState: any) => ({ ...prevState, loading: false }));
  };

  const deselectAllBySRsGroup = (data: any) => {
    setState((prevState: any) => ({ ...prevState, loading: true }));

    let selectedIds = [...state.selectedIds];
    const serviceRequestByGroupData = serviceRequestByGroup[data.id];

    if (serviceRequestByGroupData?.length > 0) {
      setState((prevState: any) => ({
        ...prevState,
        selectedIds: selectedIds.filter(
          (selected: any) =>
            serviceRequestByGroupData.findIndex(
              (sr: any) => selected.id === sr.id
            ) === -1
        ),
        loading: false,
      }));
    }
    setState((prevState: any) => ({ ...prevState, loading: false }));
  };

  const download = async () => {
    if (state.selectedIds.length > 0) {
      const selectedIdsArray = state.selectedIds.map((item: any) => item.id);
      await onDownloadCSV(selectedIdsArray);
    } else {
      props.download();
    }
    setState((prevState: any) => ({
      ...prevState,
      check: true,
      openDownloadModal: false,
    }));
  };

  const downloadPDF = async () => {
    if (state.selectedIds.length > 0) {
      const selectedIdsArray = state.selectedIds.map((item: any) => item.id);
      const selectedIdsStrings = selectedIdsArray.toString();
      const selectedIdsParams = selectedIdsStrings.replaceAll(',', '&');
      await onDownloadPDF(user.id, selectedIdsParams);
    } else {
      props.downloadPDF();
    }
    setState((prevState: any) => ({
      ...prevState,
      openDownloadModal: false,
    }));
  };

  const getBuildings = (propertyId: any) => {
    dispatch(getPropertyBuildings(propertyId));
  };

  const getBuildingsLevels = (buildingId: any) => {
    dispatch(getBuildingLevels(buildingId));
  };

  const getPropertySuites = (propertyId: any) => {
    fetchPropertySuites(propertyId);
  };

  const getPropertyCommonAreas = (propertyId: any) => {
    dispatch(fetchPropertyCommonAreas(propertyId));
  };

  const setFilterData = (value: any) => {
    setState((prevState: any) => ({ ...prevState, filterValues: value }));
    const localFilterValue = state.initialFetchServiceRequestListParams;
    localFilterValue.filterValues = value;

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: localFilterValue,
    }));

    props.filterBySearch(value);
    dispatch(addFilterQuery(value));

    let pathName = window.location.pathname;

    if (pathName.indexOf('/app/service-requests') === 0) {
      localStorage.setItem(
        'searchByFilterValues',
        value ? JSON.stringify(value) : filterQuery
      );
    }
  };

  const handleGroupByCall = () => {
    let propertyId = state.selectedServiceRequests[0].propertyId;
    let properties = state.selectedServiceRequests.filter(
      (data: any) => data.propertyId !== propertyId
    );

    let groupServiceRequestExist = serviceRequests.filter(
      (data: any) => data.requestType === 'Grouping'
    );

    setState((prevState: any) => ({
      ...prevState,
      groupServiceRequestObj: groupServiceRequestExist,
    }));

    if (properties.length > 0) {
      toast.success(
        ` ⓘ You can only group ${variables.serviceRequest.toLowerCase()}s from the same property.`,
        { autoClose: 6000, position: 'top-center' }
      );
    } else if (groupServiceRequestExist.length > 0) {
      setState((prevState: any) => ({
        ...prevState,
        openServiceRequestModal: true,
      }));
    } else {
      setState((prevState: any) => ({
        ...prevState,
        openServiceRequestModal: true,
        acceptGroupLabelInput: true,
      }));
    }
  };

  const createNewGroup = () => {
    setState((prevState: any) => ({
      ...prevState,
      openServiceRequestModal: false,
    }));
    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: {
        userID: user.id,
        role: user.roles[0].role.name,
        status: localStorage.getItem('status'),
        cdays: cdays,
        count: 1,
        text: props.searchString,
        search: props.searchString !== '' && props.searchString != null,
        filterValues: filterQuery,
      },
    }));

    let srIds = state.selectedServiceRequests.map((data: any) => {
      return {
        id: data.id,
      };
    });

    let text = `Are you sure you want to create a group for the selected ${variables.serviceRequest}?`;
    if (window.confirm(text) === true) {
      const serviceRequestsGroupingParams = {
        GroupName: state.groupLabel,
        id: null,
        PropertyId: state.selectedServiceRequests[0].propertyId,
        UserId: user.id,
        Subject: null,
        Description: null,
        SuiteId: null,
        AssetId: null,
        IssueTypeId: null,
        IssueTypeName: null,
        ServiceRequest: srIds,
      };

      dispatch(
        createServiceRequestByGroup(
          serviceRequestsGroupingParams,
          state.initialFetchServiceRequestListParams
        )
      );
    }
  };

  const addServiceRequestToGroup = (groupId: any) => {
    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: {
        userID: user.id,
        role: user.roles[0].role.name,
        status: localStorage.getItem('status'),
        cdays: cdays,
        count: 1,
        text: props.searchString,
        search: props.searchString !== '' && props.searchString != null,
        filterValues: filterQuery,
      },
    }));

    let notSameProperty = state.selectedServiceRequests.filter(
      (item: any) =>
        item.propertyId !== state.selectedGroupServiceRequest.propertyId
    );
    if (notSameProperty.length > 0) {
      setState((prevState: any) => ({
        ...prevState,
        selectedServiceRequests: [],
        selectedIds: [],
        selectedGroupServiceRequest: null,
        selectedGroupId: [],
        showGroupServiceRequest: false,
        openServiceRequestModal: false,
      }));
      toast.success(
        ` ⓘ You can only group ${variables.serviceRequest.toLowerCase()}s from the same property.`,
        { autoClose: 6000, position: 'top-center' }
      );
    } else {
      setState((prevState: any) => ({
        ...prevState,
        openServiceRequestModal: false,
      }));

      let srIds = state.selectedServiceRequests.map((data: any) => {
        return {
          id: data.id,
        };
      });

      let text = `Are you sure you want to add the selected ${variables.serviceRequest.toLowerCase()} into a existing group?`;
      if (window.confirm(text) === true) {
        const serviceRequestsGroupingParams = {
          GroupName: null,
          id: groupId,
          PropertyId: state.selectedServiceRequests[0].propertyId,
          UserId: user.id,
          Subject: null,
          Description: null,
          SuiteId: null,
          AssetId: null,
          IssueTypeId: null,
          IssueTypeName: null,
          ServiceRequest: srIds,
        };
        dispatch(
          createServiceRequestByGroup(
            serviceRequestsGroupingParams,
            state.initialFetchServiceRequestListParams
          )
        );
      }
    }
  };

  const fetchGroupServiceRequestHandler = (id: any) => {
    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: {
        userID: user.id,
        role: user.roles[0].role.name,
        status: localStorage.getItem('status'),
        cdays: cdays,
        count: 1,
        text: props.searchString,
        search: props.searchString !== '' && props.searchString != null,
        filterValues: filterQuery,
      },
    }));

    const index = serviceRequests.findIndex((x: any) => x.id === id);

    dispatch(setCurrentGroupServiceRequestIndex(index));
    dispatch(
      fetchGroupServiceRequest(
        id,
        state.initialFetchServiceRequestListParams.userID,
        state.initialFetchServiceRequestListParams.role,
        state.initialFetchServiceRequestListParams.status,
        cdays,
        state.initialFetchServiceRequestListParams.count,
        props.searchString,
        state.initialFetchServiceRequestListParams.search,
        state.initialFetchServiceRequestListParams.filterValues
      )
    );
  };

  const handleDeleteServiceRequestGroup = (data: any) => {
    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: {
        userID: user.id,
        role: user.roles[0].role.name,
        status: localStorage.getItem('status'),
        cdays: cdays,
        count: 1,
        text: props.searchString,
        search: props.searchString !== '' && props.searchString != null,
        filterValues: filterQuery,
      },
    }));

    let text = `Are you sure you want to delete the selected group of ${variables.serviceRequest}?`;

    if (window.confirm(text) === true) {
      dispatch(
        deleteServiceRequestGroup(
          data.id,
          user.id,
          state.initialFetchServiceRequestListParams
        )
      );
    }
  };

  const handleDeleteServiceRequestFromGroup = (data: any) => {
    let cdays = '0 days';

    if (localStorage.getItem('cdays') === '60+ days old') {
      cdays = '60 days';
    } else if (localStorage.getItem('cdays') === '30-60 days old') {
      cdays = '30-60 days';
    } else if (localStorage.getItem('cdays') === '<30 days old') {
      cdays = '<30 days';
    }

    setState((prevState: any) => ({
      ...prevState,
      initialFetchServiceRequestListParams: {
        userID: user.id,
        role: user.roles[0].role.name,
        status: localStorage.getItem('status'),
        cdays: cdays,
        count: 1,
        text: props.searchString,
        search: props.searchString !== '' && props.searchString != null,
        filterValues: filterQuery,
      },
    }));

    let text = `Are you sure you want to remove the selected ${variables.serviceRequest} from the current group?`;

    if (window.confirm(text) === true) {
      dispatch(
        deleteServiceRequestFromGroup(
          data.id,
          user.id,
          state.initialFetchServiceRequestListParams
        )
      );
    }
  };

  const {
    from,
    onSearch,
    serviceRequest,
    itemView,
    strapline,
    mainServiceRequest,
    searchString,
    introContent,
  } = props;

  let sortableServiceRequest = serviceRequests.map((sr: any) => ({
    ...sr,
    sortableUnitName: suiteName(sr, from),
    sortableCommonAreaName: commonAreaName(sr),
    sortableIssueType: sortableType(sr),
  }));

  const tableColumns: any = useMemo(
    () =>
      generateServiceRequestTableColumns({
        user,
        variables,
        from,
        PATHS,
        ICONS,
        USER_ROLES,
        props,
        state,
        setState,
        isGroupExpanded,
        expandGroup,
        collapseGroup,
        selectAllBySRsGroup,
        deselectAllBySRsGroup,
        fetchGroupServiceRequestHandler,
        handleDeleteServiceRequestGroup,
        onClickBulkUpdate,
        serviceRequestTableLocalTime,
        sortByDate,
        moment,
        Checkbox,
        Link,
        Status,
        FontAwesomeIcon,
        IcoMoon,
        Label,
      }),
    [
      user.roles,
      variables,
      from,
      state.selectedIds,
      state.selectedSrsGroupId,
      state.selectedGroupServiceRequestId,
    ]
  );

  return (
    <>
      {((props.isCreateRequestDisabled &&
        user.roles?.[0]?.role?.name === 'Home Owner') ||
        introContent) && (
        <ContentBlock
          content={
            props.isCreateRequestDisabled &&
            user.roles?.[0]?.role?.name === 'Home Owner' ? (
              <WarrantyDisabledMessage
                warrantyStartDate={serviceRequest?.asset_warranty_star_date}
              />
            ) : (
              IntroContent.serviceRequests
            )
          }
        />
      )}
      <SearchAndActionsBar
        searchString={searchString}
        onSearch={onSearch}
        user={user}
        allowedStatuses={allowedStatuses}
        selectedIds={state.selectedIds}
        isGroupSelected={state.isGroupSelected}
        status={state.status}
        onChangeStatus={onChangeStatus}
        onOpenFilters={() =>
          setState((prevState: any) => ({
            ...prevState,
            openFiltersModal: true,
          }))
        }
        onGroupBy={handleGroupByCall}
        selectAll={state.selectAll}
        onSelectAll={selectAll}
        serviceRequests={serviceRequests}
        onOpenDownloadModal={() =>
          setState((prevState: any) => ({
            ...prevState,
            openDownloadModal: true,
          }))
        }
        isCreateRequestDisabled={props.isCreateRequestDisabled}
        dispatch={dispatch}
        from={from}
        displayClosedAndResolvedTickets={displayClosedAndResolvedTickets}
        filterQuery={filterQuery}
        onFilterBySearch={props.filterBySearch}
        itemView={itemView}
        mainServiceRequest={mainServiceRequest}
      />
      <ActiveFiltersDisplay
        from={from}
        filterQuery={filterQuery}
        properties={properties}
        selectedPropertyCommonAreas={selectedPropertyCommonAreas}
        items={items}
        buildingsList={buildingsList}
        levelsList={levelsList}
        filterServiceProviderandPropertyDeveloperList={
          filterServiceProviderandPropertyDeveloperList
        }
        setFilterData={setFilterData}
      />
      {csvExportLoading === true ? <Loader center={true} /> : null}
      {serviceRequests && serviceRequests.length > 0 ? (
        <>
          {formLoader || state.loading || isLoading === true ? (
            <div
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <Loader center={true} />
            </div>
          ) : null}
          <ServiceRequestsTable
            serviceRequests={sortableServiceRequest}
            hasMoreRecords={state.hasMoreRecords}
            paginationCount={paginationCount}
            pageCount={pageCount}
            fetchMoreData={fetchMoreData}
            indexID={serviceRequestByGroupIndex}
            selectedIds={state.selectedIds}
            selectedServiceRequests={state.selectedServiceRequests}
            expandedGroups={state.expandedGroupIds}
            from={from}
            applyMinWidth={true}
            fetchGroupServiceRequest={(id: any, data: any) =>
              fetchGroupServiceRequestHandler(id)
            }
            onClickBulkUpdate={(id: any, data: any) =>
              onClickBulkUpdate(id, data)
            }
            handleDeleteServiceRequestFromGroup={(data: any) =>
              handleDeleteServiceRequestFromGroup(data)
            }
            columns={tableColumns}
          />
        </>
      ) : (
        <EmptyState
          user={user}
          variables={variables}
          search={search}
          strapline={strapline}
          itemView={itemView}
          dispatch={dispatch}
        />
      )}
      {/* Filter Modal.*/}
      <Modal
        open={state.openFiltersModal}
        onClose={() =>
          setState((prevState: any) => ({
            ...prevState,
            openFiltersModal: false,
          }))
        }
        modal={true}
        closeOnDocumentClick={false}
        className="popup-content"
      >
        <FilterModal
          onClose={() =>
            setState((prevState: any) => ({
              ...prevState,
              openFiltersModal: false,
            }))
          }
          filterValues={filterQuery}
          setFilterData={(value: any) => setFilterData(value)}
          properties={properties}
          fetchedPropertyCommonAreas={fetchedPropertyCommonAreas}
          issueTypes={issueTypes}
          assetTypes={items}
          getPropertySuites={(propertyId: any) => {
            getPropertySuites(propertyId);
            getPropertyCommonAreas(propertyId);
          }}
          propertySuites={data.suitesList}
          getBuildings={(propertyId: any) => getBuildings(propertyId)}
          buildingsList={buildingsList}
          getBuildingsLevels={(buildingId: any) =>
            getBuildingsLevels(buildingId)
          }
          levelsList={levelsList}
        />
      </Modal>
      {/* Download Modal. */}
      <DownloadCSVModalLegacy
        isOpen={state.openDownloadModal}
        onClose={() =>
          setState((prevState: any) => ({
            ...prevState,
            openDownloadModal: false,
          }))
        }
        onDownloadCSV={download}
        onDownloadPDF={downloadPDF}
      />
      {/* Grouping Modal. */}
      <GroupingModal
        isOpen={state.openServiceRequestModal}
        onClose={() =>
          setState((prevState: any) => ({
            ...prevState,
            openServiceRequestModal: false,
            selectedGroupId: '',
          }))
        }
        acceptGroupLabelInput={state.acceptGroupLabelInput}
        groupLabel={state.groupLabel}
        onGroupLabelChange={(e: any) =>
          setState((prevState: any) => ({
            ...prevState,
            groupLabel: e.target.value,
          }))
        }
        onCreateNewGroup={createNewGroup}
        showGroupServiceRequest={state.showGroupServiceRequest}
        groupServiceRequestObj={state.groupServiceRequestObj}
        selectedGroupId={state.selectedGroupId}
        onGroupSelect={(data: any) => {
          setState((prevState: any) => ({
            ...prevState,
            selectedGroupId: data.id,
          }));
          onClickBulkUpdate(data.id, data);
          setState((prevState: any) => ({
            ...prevState,
            selectedGroupServiceRequest: data,
          }));
        }}
        onAddToGroup={addServiceRequestToGroup}
        selectedGroupServiceRequest={state.selectedGroupServiceRequest}
        onShowGroupInput={() =>
          setState((prevState: any) => ({
            ...prevState,
            acceptGroupLabelInput: true,
          }))
        }
        onShowGroupSelection={() =>
          setState((prevState: any) => ({
            ...prevState,
            showGroupServiceRequest: true,
          }))
        }
      />
      {/* Overlays and Popups. */}
      <Overlay
        showOverlay={serviceRequestFormIsVisible}
        content={
          <ServiceRequestForm
            propertyDisabled={props.propertyDisabled}
            serviceRequest={serviceRequest}
            from={from}
          />
        }
      />
      <Overlay
        showOverlay={state.showOverlay}
        content={
          <ServiceRequestAssignForm
            from={from}
            serviceRequest={
              state.isGroupSelected
                ? state.selectedSrsGroupId[0]
                : state.overlayServiceRequest[0]
            }
            onClose={() =>
              setState((prevState: any) => ({
                ...prevState,
                showOverlay: false,
              }))
            }
            handleStatusChange={handleStatus}
            isLegacyTable={true}
          />
        }
      />
      {state.showPopup ? <Popup onSubmitPopup={onSubmitPopup} /> : null}
    </>
  );
};

export default ServiceRequestsTableMain;
